/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import * as React from "react";
import styled from "styled-components";
import { SVGIcon, Text, theme, FlexContainer } from "@nordcloud/gnui";
import { IconName } from "~/types/IconName";

type WrapperProps = {
  active: boolean;
};

const Wrapper = styled.button<WrapperProps>`
  display: flex;
  place-items: center;
  background-color: ${theme.color.background.ui03};
  height: 40px;
  transition: background-color 0.3s ease;
  border: ${theme.borderDefault};
  padding: ${theme.spacing.spacing02};
  border-radius: ${theme.radius.lg};
  border-color: ${({ active }) =>
    active ? theme.color.border.focus : theme.color.border.border01};
  filter: ${({ active }) => (active ? "none" : "grayscale(100%)")};
  cursor: pointer;

  &:hover {
    background-color: ${theme.color.background.ui04};
  }
`;

type Item = {
  id: string;
  iconName?: IconName;
  text?: string;
  label?: string;
};

type Props<T extends Item> = {
  items: readonly T[];
  selected: T["id"][] | undefined;
  onSelect: (itemId: T["id"]) => void;
};

export function IconSelector<T extends Item>({
  items,
  selected,
  onSelect,
}: Props<T>) {
  return (
    <FlexContainer gap={theme.spacing.spacing03}>
      {items.map((item) => {
        const isSelected = selected?.includes(item.id);
        return (
          <React.Fragment key={item.id}>
            <Wrapper
              active={!!isSelected}
              onClick={() => onSelect(item.id)}
              aria-label={item.label || item.text || item.iconName}
              aria-selected={isSelected}
              role="option"
              type="button"
            >
              {item.iconName && <SVGIcon name={item.iconName} size="md" />}
              <Text tag="span">{item?.text}</Text>
            </Wrapper>
          </React.Fragment>
        );
      })}
    </FlexContainer>
  );
}
